import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Prompt } from 'react-router-dom';

import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import { setUnsavedChangesPromptHandler } from 'router/unsavedChangesPromptRegistry';

type UseUnsavedChangesProtectionOptions = {
  isDirty: boolean;
  onSave: () => Promise<void>;
  onNavigate: () => void;
  enabled?: boolean;
};

type UseUnsavedChangesProtectionReturn = {
  allowNextNavigation: () => void;
  handleNavigation: () => Promise<void>;
  NavigationPrompt: React.ComponentType;
};

/**
 * Hook to protect against navigation when there are unsaved changes.
 * Provides a handler for programmatic navigation and a Prompt component for browser navigation.
 *
 * @param options - Configuration options
 * @param options.isDirty - Whether the form has unsaved changes
 * @param options.onSave - Function to save the form
 * @param options.onNavigate - Function to perform navigation
 * @param options.enabled - Whether the protection is enabled (default: true)
 * @returns Object with handleNavigation function and NavigationPrompt component
 */
export const useUnsavedChangesProtection = ({
  isDirty,
  onSave,
  onNavigate,
  enabled = true,
}: UseUnsavedChangesProtectionOptions): UseUnsavedChangesProtectionReturn => {
  const { showUnsavedChangesModal } = useConfirmationModalContext();
  const [shouldBlock, setShouldBlock] = useState(false);
  const isProcessingRef = useRef(false);
  const allowNextNavigationRef = useRef(false);

  const allowNextNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
  }, []);

  const handleNavigation = useCallback(async () => {
    if (!enabled || !isDirty || isProcessingRef.current) {
      onNavigate();
      return;
    }

    try {
      isProcessingRef.current = true;
      setShouldBlock(false); // Temporarily allow navigation for our modal
      const action = await showUnsavedChangesModal({});

      if (action === 'save') {
        await onSave();
        setShouldBlock(false);
        onNavigate();
      } else if (action === 'discard') {
        setShouldBlock(false);
        onNavigate();
      } else {
        // 'cancel' - stay on page
        setShouldBlock(isDirty);
      }
    } catch (error) {
      // If save fails, stay on page
      setShouldBlock(isDirty);
    } finally {
      isProcessingRef.current = false;
    }
  }, [enabled, isDirty, onSave, onNavigate, showUnsavedChangesModal]);

  // Update shouldBlock when isDirty changes
  useEffect(() => {
    if (enabled) {
      setShouldBlock(isDirty && !isProcessingRef.current);
    }
  }, [enabled, isDirty]);

  // Register handler for React Router navigation (menu, Link, logout, etc.) so our
  // Unsaved changes modal is shown instead of the default browser confirm.
  useEffect(() => {
    const cleanup = () => setUnsavedChangesPromptHandler(null);

    if (!enabled || !shouldBlock || isProcessingRef.current) {
      setUnsavedChangesPromptHandler(null);
      return cleanup;
    }

    const handler = (message: string, callback: (ok: boolean) => void) => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        callback(true);
        return;
      }
      isProcessingRef.current = true;
      setShouldBlock(false);
      showUnsavedChangesModal({})
        .then(action => {
          if (action === 'save') {
            onSave()
              .then(() => {
                setShouldBlock(false);
                callback(true);
              })
              .catch(() => {
                setShouldBlock(isDirty);
                callback(false);
              });
          } else if (action === 'discard') {
            setShouldBlock(false);
            callback(true);
          } else {
            setShouldBlock(isDirty);
            callback(false);
          }
        })
        .catch(() => {
          setShouldBlock(isDirty);
          callback(false);
        })
        .finally(() => {
          isProcessingRef.current = false;
        });
    };

    setUnsavedChangesPromptHandler(handler);

    return cleanup;
  }, [enabled, shouldBlock, isDirty, onSave, showUnsavedChangesModal]);

  // Note: We do NOT use history.block() here. The history package supports only one prompt
  // at a time. Our <Prompt> (in NavigationPrompt) is that single block—it handles all
  // transitions (Link clicks/PUSH, browser back/POP, replace). Using history.block() here
  // would overwrite the Prompt and break menu/sidebar navigation (e.g. Articles).

  // Handle keyboard shortcuts for refresh (F5, Ctrl+R, Cmd+R)
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!enabled || !isDirty || isProcessingRef.current) {
      return;
    }

    const handleKeyDown = async (e: KeyboardEvent) => {
      // Check for F5, Ctrl+R / Cmd+R, or Shift+Ctrl+R / Shift+Cmd+R (hard refresh)
      const isRefresh =
        e.key === 'F5' ||
        (e.key === 'r' && (e.ctrlKey || e.metaKey) && !e.shiftKey) ||
        (e.key === 'R' && (e.ctrlKey || e.metaKey) && e.shiftKey);

      if (isRefresh) {
        e.preventDefault();
        e.stopPropagation();

        try {
          isProcessingRef.current = true;
          const action = await showUnsavedChangesModal({});

          if (action === 'save') {
            await onSave();
            // After save, allow refresh by removing the handler temporarily
            window.onbeforeunload = null;
            // Trigger refresh
            window.location.reload();
          } else if (action === 'discard') {
            // Allow refresh by removing the handler temporarily
            window.onbeforeunload = null;
            // Trigger refresh
            window.location.reload();
          }
          // 'cancel' - do nothing, stay on page
        } catch (error) {
          // If save fails, stay on page
        } finally {
          isProcessingRef.current = false;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, isDirty, onSave, showUnsavedChangesModal]);

  // Handle page refresh/close (close tab, browser close, etc.) using window.onbeforeunload
  // Note: This shows native browser dialog, not our custom modal, as onbeforeunload
  // cannot show custom React components. For F5/Ctrl+R, we handle it above with custom modal.
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!enabled || !isDirty || isProcessingRef.current) {
      // Remove handler if form is clean or protection is disabled
      window.onbeforeunload = null;
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Modern browsers ignore custom messages, but we still need to set returnValue
      // or return a string to trigger the native browser dialog
      e.preventDefault();
      // Modern browsers will show their own message, but we set returnValue for compatibility
      e.returnValue = '';
      return '';
    };

    window.onbeforeunload = handleBeforeUnload;

    const cleanup = () => {
      window.onbeforeunload = null;
    };
    // eslint-disable-next-line consistent-return
    return cleanup;
  }, [enabled, isDirty]);

  const NavigationPrompt: React.FC = useCallback(() => {
    if (!enabled || !shouldBlock) {
      return null;
    }
    // Return a string so React Router calls getUserConfirmation(message, callback).
    // BrowserRouter's getUserConfirmation uses our registered handler, which shows the Unsaved changes modal.
    return <Prompt message='unsaved_changes' when={shouldBlock} />;
  }, [enabled, shouldBlock]);

  return {
    allowNextNavigation,
    handleNavigation,
    NavigationPrompt,
  };
};

export default useUnsavedChangesProtection;
